import { ProjectMoveMenu } from '@components/ProjectMoveMenu'
import { ProjectStatusMenu } from '@components/ProjectStatusMenu'
import { DropdownMenu } from '@octopus-do/octopus-storybook'
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  Files,
} from '@octopus-do/octopus-storybook/icons'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { selectProjectParents } from '@store/reducers/dashboard/selectors'
import type { ProjectMeta } from '@store/reducers/manager/meta/types'
import type { AppState } from '@store/types'
import type React from 'react'
import { useSelector } from 'react-redux'

import { isPaidPlan } from '@/constants/userSubscriptionPlans'
import { useAuth, useProjectAccess } from '@/hooks'
import { EXPORT_PERMISSION } from '@/permissions/project/definitions'
import { ExportContent, FreeExportContent } from './components'

export const FileContent: React.FC = () => {
  const { space, team } = useSelector<AppState, ProjectMeta>(
    (state) => state.manager.meta
  )

  const { planId } = useAuth()

  const isTemplate = useSelector(migrationProjectSelector.getProjectIsTemplate)
  const canEdit = useSelector(migrationProjectSelector.getProjectAccessCanEdit)

  const { hasAccess: exportable } = useProjectAccess(
    EXPORT_PERMISSION.FILE_FORMATS
  )

  const projectId = useSelector(migrationProjectSelector.getProjectId)

  const parents = useSelector(selectProjectParents(projectId ?? ''))

  // canEdit reads true until meta has loaded, so the project id is what says the answer
  // can be trusted. Import writes pages — offering it to a viewer would be a lie.
  const importable = Boolean(projectId) && canEdit

  const content = exportable ? ExportContent : FreeExportContent

  // Any paid plan may save templates into its own workspace, and membership
  // of a team lets you save into that team whatever your plan — which is why
  // a project that already sits in a team counts too. Free gets the PRO badge
  // and the upgrade modal.
  const canCreateTemplates = isPaidPlan(planId) || Boolean(team)

  return (
    <>
      {importable && (
        // Plain button, not preventClose: this opens a modal, so the dropdown should go.
        <DropdownMenu.Button
          icon={ArrowUpFromLine}
          id="import"
          title="Import"
        />
      )}
      <DropdownMenu.NestedMenu
        content={content}
        icon={ArrowDownToLine}
        id="export"
        title="Export"
      />

      {/* A template of a template is not a thing: the item disappears once the
          open project is itself a template. */}
      {!isTemplate && canEdit && canCreateTemplates && (
        <DropdownMenu.Button
          icon={Files}
          id="save_as_template"
          title="Save template"
        />
      )}

      {!isTemplate && canEdit && !canCreateTemplates && (
        <DropdownMenu.BadgedButton
          icon={Files}
          id="save_as_template_upgrade"
          title="Save template"
          type="pro"
        />
      )}

      {!isTemplate && canEdit && parents && (
        <ProjectStatusMenu project={parents.project} />
      )}

      <ProjectMoveMenu
        currentFolder={null}
        folders={[]}
        disabled={false}
        workspaces={space.workspaces}
      />
    </>
  )
}
